"use client";

import { UnifiedChatBell } from "@/components/chat/UnifiedChatBell";
import { useAuth } from "@/contexts/AuthContext";

import { useUserTeams } from "@/hooks/useUserTeams";
import { useUserTournaments } from "@/hooks/useUserTournaments";
import {
  getMenuNavItems,
  isActivePath,
  Link,
  usePathname,
} from "@/i18n/routing";
import { isUserAdmin } from "@/lib/users";
import { Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocale, useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { useMemo, useState } from "react";
import { MobileHeaderBar } from "./nav/MobileHeaderBar";
import { NavDrawer } from "./nav/NavDrawer";
import { TowerChallengerLogo } from "./TowerChallengerLogo";
import { HeaderButton, UserMenu } from "./UserMenu";

export type TournamentNavItem = {
  [tournamentId: string]: { name: string; slug: string };
};

export type TeamNavItem = {
  teamId: string;
  teamName: string | undefined;
  tournamentSlug: string;
  tournamentName: string;
};

export function PublicHeader() {
  const t = useTranslations();
  const { user, loading: userLoading } = useAuth();
  const { data: tournaments = [], isLoading: tournamentsLoading } =
    useUserTournaments(user?.tournamentIds);
  const { data: userTeams = [] } = useUserTeams();
  const locale = useLocale() as "fi" | "en";
  const pathname = usePathname();
  const params = useParams<{ tournamentSlug?: string }>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [towerTooltipOpen, setTowerTooltipOpen] = useState(false);

  // Tournament pages own their entire mobile chrome via `MobileNav` (which
  // already renders `MobileHeaderBar` + a tournament-context row). Hide
  // this header on tournament paths so the two don't stack.
  const isTournamentPath = !!params?.tournamentSlug;

  // Get navigation items from routing config
  const mainNavItems = getMenuNavItems("main");

  const tournamentNavItems: TournamentNavItem[] = useMemo(() => {
    if (tournamentsLoading || !tournaments) return [];

    return tournaments.map((tournament) => ({
      [tournament.id]: {
        name: tournament.name,
        slug: tournament.slug,
      },
    }));
  }, [tournaments, tournamentsLoading]);

  const teamNavItems: TeamNavItem[] = useMemo(() => {
    return userTeams.map((team) => ({
      teamId: team.teamId,
      teamName: team.teamName,
      tournamentSlug: team.tournamentSlug,
      tournamentName: team.tournamentName,
    }));
  }, [userTeams]);

  // Get the first available tournament slug for the "My Home" button
  const firstTournamentSlug = useMemo(() => {
    if (teamNavItems.length > 0) {
      return teamNavItems[0].tournamentSlug;
    }
    if (tournamentNavItems.length > 0) {
      const firstTournament = tournamentNavItems[0];
      const keys = Object.keys(firstTournament);
      if (keys.length > 0) {
        return firstTournament[keys[0]]?.slug;
      }
    }
    return null;
  }, [teamNavItems, tournamentNavItems]);

  const isOnOwnTowerPage =
    pathname === "/tournaments/[slug]" &&
    !!params?.tournamentSlug &&
    params.tournamentSlug === firstTournamentSlug;

  return (
    <>
      <NavDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Mobile header — same chrome as tournament pages, minus row 2 */}
      {!isTournamentPath && (
        <header
          className="fixed inset-x-0 top-0 z-50 bg-tn-bg-nav lg:hidden"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(250,204,21,0.4), transparent)",
            }}
          />
          <MobileHeaderBar onOpenDrawer={() => setDrawerOpen(true)} />
        </header>
      )}

      {/* Desktop header */}
      <header
        className={
          "fixed top-0 left-0 right-0 z-50 bg-[var(--bg-header)] backdrop-blur-[20px] border-b border-[var(--border-gold)] h-[72px] md:h-[76px] hidden lg:block " +
          (isTournamentPath ? "lg:hidden" : "")
        }
      >
        <div className="header-content max-w-[1600px] mx-auto px-4 md:px-6 h-full flex items-center justify-between relative z-10">
          <Link href="/" className="flex items-center">
            <TowerChallengerLogo size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-4 2xl:gap-8">
            {mainNavItems.map((item) => {
              const isExactMatch = item.pathnames[locale] === "/";
              const isActive = isActivePath(
                pathname,
                item.pathnames[locale],
                isExactMatch,
              );
              return (
                <Link
                  key={item.pathnames[locale]}
                  href={item.pathnames[locale]}
                  aria-current={isActive ? "page" : undefined}
                  className="nav-link relative no-underline font-cinzel font-semibold text-[0.8rem] 2xl:text-[0.9rem] tracking-[0.04em] 2xl:tracking-[0.06em] transition-colors py-2 text-secondary hover:text-[var(--gold-light)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-gradient-to-r after:from-transparent after:via-[var(--gold)] after:to-transparent after:opacity-0 after:transition-opacity hover:after:opacity-100 aria-[current=page]:after:opacity-100 whitespace-nowrap"
                >
                  {item.label[locale]}
                </Link>
              );
            })}
          </nav>

          <div className="header-actions flex items-center gap-1.5 md:gap-2">
            {userLoading ? (
              <div
                role="status"
                aria-label={t("common.loading")}
                className="flex items-center gap-1.5 md:gap-2"
              >
                <div className="h-10 w-10 rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-card)] opacity-50 animate-pulse" />
                <div className="h-10 w-10 rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-card)] opacity-50 animate-pulse" />
                <div className="h-10 w-[120px] rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-card)] opacity-50 animate-pulse" />
              </div>
            ) : user ? (
              <>
                {/* Home button - links to first team's tournament */}
                {firstTournamentSlug && (
                  <Tooltip
                    content={t("header.nav.myHome")}
                    size="md"
                    isOpen={towerTooltipOpen}
                    onOpenChange={setTowerTooltipOpen}
                  >
                    {isOnOwnTowerPage ? (
                      <HeaderButton
                        aria-current="page"
                        onClick={() => setTowerTooltipOpen(false)}
                        className="bg-transparent text-secondary border-[var(--border-subtle)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
                      >
                        <Icon
                          icon="game-icons:stone-tower"
                          className="text-2xl"
                        />
                      </HeaderButton>
                    ) : (
                      <Link
                        href={`/tournaments/${firstTournamentSlug}`}
                        onClick={() => setTowerTooltipOpen(false)}
                      >
                        <HeaderButton className="bg-transparent text-secondary border-[var(--border-subtle)] hover:border-[var(--gold)] hover:text-[var(--gold)]">
                          <Icon
                            icon="game-icons:stone-tower"
                            className="text-2xl"
                          />
                        </HeaderButton>
                      </Link>
                    )}
                  </Tooltip>
                )}

                <Tooltip content={t("header.chat.title")} size="md">
                  <UnifiedChatBell />
                </Tooltip>

                <UserMenu
                  userName={user.name}
                  tournamentNavItems={tournamentNavItems}
                  teamNavItems={teamNavItems}
                  isOrganizer={isUserAdmin(user)}
                />
              </>
            ) : (
              <>
                <Link href="/login">
                  <HeaderButton className="bg-gradient-to-br from-[var(--gold)] to-[var(--gold-dark)] rounded-[10px] border-[var(--gold-light)] ml-1 cursor-pointer transition-transform hover:scale-105">
                    {t("auth.login.signIn")}
                  </HeaderButton>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
